import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MagnifyingGlass, Plus, Trash, MapPinLine, CircleNotch, Buildings } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Debounce hook — waits `delay` ms after last change before returning the new value.
 */
function useDebounce(value, delay = 500) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export default function Citta() {
  const [query, setQuery] = useState("");
  const debounced = useDebounce(query, 500);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [cities, setCities] = useState([]);
  const [adding, setAdding] = useState({});

  const refreshCities = () => api.get("/cities").then((r) => setCities(r.data));

  useEffect(() => { refreshCities(); }, []);

  useEffect(() => {
    if (!debounced || debounced.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    api.get(`/cities/search?q=${encodeURIComponent(debounced)}`)
      .then((r) => setResults(r.data || []))
      .catch(() => setResults([]))
      .finally(() => setSearching(false));
  }, [debounced]);

  const addCity = async (result) => {
    setAdding((a) => ({ ...a, [result.display_name]: true }));
    try {
      const { data } = await api.post("/cities/custom", {
        name: result.name,
        lat: result.lat,
        lng: result.lng,
        display_name: result.display_name,
        place_type: result.type,
      });
      if (data.already_existed) toast.info(`${result.name} è già presente`);
      else toast.success(`${result.name} aggiunta al catalogo`);
      refreshCities();
    } catch (e) {
      toast.error("Errore nell'aggiunta della città");
    } finally {
      setAdding((a) => { const c = { ...a }; delete c[result.display_name]; return c; });
    }
  };

  const removeCity = async (id) => {
    if (!confirm("Rimuovere questa città e i suoi dati?")) return;
    try {
      await api.delete(`/cities/custom/${id}`);
      toast.success("Città rimossa");
      refreshCities();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const defaultCities = useMemo(() => cities.filter((c) => !c.custom), [cities]);
  const customCities = useMemo(() => cities.filter((c) => c.custom), [cities]);

  return (
    <div className="min-h-[calc(100vh-56px)] bg-zinc-950">
      <div className="border-b border-white/10 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <Buildings size={22} weight="bold" className="text-lime-400" />
            <div className="font-mono-data text-xs text-lime-400 tracking-widest">— GESTIONE CITTÀ</div>
          </div>
          <h1 className="font-display font-black text-3xl mb-2">Aggiungi qualsiasi località italiana.</h1>
          <p className="text-sm text-zinc-400 max-w-2xl">
            Cerca comuni, frazioni o quartieri via OpenStreetMap (Nominatim) e aggiungili al catalogo.
            Una volta aggiunti, appariranno in tutte le sezioni: mappa, inserzioni, aste, ricerca, confronto.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6 grid lg:grid-cols-[1fr_1fr] gap-6">
        {/* Search */}
        <div className="border border-white/10 p-5">
          <div className="flex items-center gap-2 mb-4">
            <MagnifyingGlass size={16} weight="bold" className="text-lime-400" />
            <span className="font-mono-data text-xs text-lime-400 tracking-widest">CERCA LOCALITÀ</span>
          </div>
          <div className="relative">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="es. Bologna, Ravenna, Val di Fassa, Positano..."
              className="rounded-none bg-zinc-900 border-white/10 h-11 pl-4 pr-10 font-mono-data text-sm"
              data-testid="city-search-input"
              autoFocus
            />
            {searching && <CircleNotch size={16} weight="bold" className="animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-lime-400" />}
          </div>

          <div className="mt-4 divide-y divide-white/5 max-h-[500px] overflow-y-auto" data-testid="city-search-results">
            {debounced.length >= 2 && !searching && results.length === 0 && (
              <div className="p-6 text-center font-mono-data text-xs text-zinc-500">
                Nessun risultato per "{debounced}"
              </div>
            )}
            {debounced.length < 2 && (
              <div className="p-6 text-center font-mono-data text-xs text-zinc-600">
                Digita almeno 2 lettere per cercare
              </div>
            )}
            {results.map((r) => (
              <div key={`${r.display_name}-${r.lat}`} className="py-3 flex items-start gap-3" data-testid={`search-result-${r.name}`}>
                <MapPinLine size={16} weight="bold" className="text-lime-400 mt-1 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-base flex items-center gap-2 flex-wrap">
                    {r.name}
                    {r.type && <Badge className="rounded-none bg-zinc-800 text-zinc-400 font-mono-data text-[10px] hover:bg-zinc-800">{r.type}</Badge>}
                  </div>
                  <div className="font-mono-data text-[11px] text-zinc-500 truncate">{r.display_name}</div>
                  <div className="font-mono-data text-[10px] text-zinc-600">{r.lat.toFixed(4)}, {r.lng.toFixed(4)}</div>
                </div>
                <Button
                  size="sm"
                  onClick={() => addCity(r)}
                  disabled={!!adding[r.display_name]}
                  className="rounded-none bg-lime-400 text-lime-950 hover:bg-lime-500 font-bold h-8 text-xs"
                  data-testid={`btn-add-${r.name}`}
                >
                  {adding[r.display_name] ? <CircleNotch size={12} weight="bold" className="animate-spin" /> : <><Plus size={12} weight="bold" className="mr-1" />Aggiungi</>}
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Catalog */}
        <div className="border border-white/10 p-5">
          <div className="flex items-center gap-2 mb-4">
            <MapPinLine size={16} weight="bold" className="text-cyan-400" />
            <span className="font-mono-data text-xs text-cyan-400 tracking-widest">CATALOGO CITTÀ ({cities.length})</span>
          </div>

          <div className="font-mono-data text-[10px] text-zinc-500 tracking-widest mb-2">DEFAULT ({defaultCities.length})</div>
          <div className="divide-y divide-white/5 mb-6">
            {defaultCities.map((c) => (
              <div key={c.id} className="py-2.5 flex items-center justify-between" data-testid={`city-default-${c.id}`}>
                <div className="font-display font-bold text-sm">{c.name}</div>
                <Badge className="rounded-none bg-zinc-800 text-zinc-400 font-mono-data text-[10px] hover:bg-zinc-800">DEFAULT</Badge>
              </div>
            ))}
          </div>

          <div className="font-mono-data text-[10px] text-lime-400 tracking-widest mb-2">PERSONALIZZATE ({customCities.length})</div>
          <div className="divide-y divide-white/5" data-testid="custom-cities-list">
            {customCities.length === 0 ? (
              <div className="py-6 text-center font-mono-data text-xs text-zinc-500">Nessuna città personalizzata. Cerca e aggiungi la prima.</div>
            ) : customCities.map((c) => (
              <div key={c.id} className="py-2.5 flex items-center justify-between" data-testid={`city-custom-${c.id}`}>
                <div>
                  <div className="font-display font-bold text-sm">{c.name}</div>
                  <div className="font-mono-data text-[10px] text-zinc-600">{c.center[0].toFixed(4)}, {c.center[1].toFixed(4)}</div>
                </div>
                <button onClick={() => removeCity(c.id)} className="text-zinc-500 hover:text-red-400 p-2" data-testid={`btn-remove-${c.id}`}>
                  <Trash size={14} weight="bold" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
